import React, { useEffect, useState } from 'react';
import { useEditor } from '../store/EditorContext';
import { FieldType, InputControl, DataSourceItem, ModifierType, ConditionOperator, Modifier, Condition, ParameterConfiguration } from '../types';
import { Settings, Sliders, AlertCircle, Plus, X, GitBranch, Trash2, Folder, Database, FileText, Blocks } from 'lucide-react';
import { DataSourceBuilder } from './DataSourceBuilder';

export const PropertyPanel: React.FC = () => {
  const { state, dispatch } = useEditor();
  const { selectedElementId, selectedElementType } = state;

  const selectedField = selectedElementType === 'field' 
    ? state.form.fields.find(f => f.id === selectedElementId) 
    : null;
    
  const selectedGroup = selectedElementType === 'group'
    ? state.form.groups.find(g => g.id === selectedElementId)
    : null;

  const [localId, setLocalId] = useState('');
  const [idError, setIdError] = useState<string | null>(null);
  const [datasourceMode, setDatasourceMode] = useState<'static' | 'dynamic'>('static');
  const [newExtKey, setNewExtKey] = useState('');
  const [newExtValue, setNewExtValue] = useState('');

  useEffect(() => {
    if (selectedField) {
        setLocalId(selectedField.id);
        setIdError(null);
        if (selectedField.datasourceString && (!selectedField.dataSource || selectedField.dataSource.length === 0)) {
            setDatasourceMode('dynamic');
        } else {
            setDatasourceMode('static');
        }
        setNewExtKey('');
        setNewExtValue('');
    } else if (selectedGroup) {
        setLocalId(selectedGroup.id);
        setIdError(null);
    }
  }, [selectedField?.id, selectedGroup?.id]);

  const handleFieldUpdate = (updates: Partial<typeof selectedField>) => {
      if (selectedField) {
        dispatch({ type: 'UPDATE_FIELD', payload: { id: selectedField.id, updates } });
      }
  };

  const handleGroupUpdate = (updates: Partial<typeof selectedGroup>) => {
      if (selectedGroup) {
          dispatch({ type: 'UPDATE_GROUP', payload: { id: selectedGroup.id, updates } });
      }
  }

  const handleIdChange = (newId: string) => {
      setLocalId(newId);
      if (!newId.trim()) {
          setIdError('ID cannot be empty');
          return;
      }
      if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(newId)) {
          setIdError('ID must start with a letter and contain only alphanumeric chars or _');
          return;
      }
      const exists = state.form.fields.some(f => f.id === newId && f.id !== selectedElementId) || 
                     state.form.groups.some(g => g.id === newId && g.id !== selectedElementId);
      if (exists) {
          setIdError('ID must be unique');
          return;
      }
      setIdError(null);
      if (selectedElementType === 'field') {
        handleFieldUpdate({ id: newId });
      } else {
        handleGroupUpdate({ id: newId });
      }
  };

  const addOption = () => {
      if (!selectedField) return;
      const newOption: DataSourceItem = { key: `opt_${Date.now()}`, value: 'New Option' };
      handleFieldUpdate({
          dataSource: [...(selectedField.dataSource || []), newOption]
      });
  };

  const removeOption = (idx: number) => {
      if (!selectedField) return;
      const newSource = [...(selectedField.dataSource || [])];
      newSource.splice(idx, 1);
      handleFieldUpdate({ dataSource: newSource });
  };

  const updateOption = (idx: number, key: keyof DataSourceItem, val: string) => {
    if (!selectedField) return;
    const newSource = [...(selectedField.dataSource || [])];
    newSource[idx] = { ...newSource[idx], [key]: val };
    handleFieldUpdate({ dataSource: newSource });
  };

  const updateParameterConfig = (key: keyof ParameterConfiguration, value: string) => {
    if (!selectedField) return;
    const currentConfig = selectedField.parameterConfig || { routeKey: '', allowedEntities: '' };
    handleFieldUpdate({
        parameterConfig: { ...currentConfig, [key]: value }
    });
  };

  const addExtension = () => {
    if (!selectedField || !newExtKey) return;
    const current = selectedField.extensions || {};
    handleFieldUpdate({
        extensions: { ...current, [newExtKey]: newExtValue }
    });
    setNewExtKey('');
    setNewExtValue('');
  };

  const removeExtension = (key: string) => {
     if (!selectedField || !selectedField.extensions) return;
     const newExt = { ...selectedField.extensions };
     delete newExt[key];
     handleFieldUpdate({ extensions: newExt });
  };

  const addModifier = () => {
    if (!selectedField) return;
    const newModifier: Modifier = {
      id: `mod_${Date.now()}`,
      type: ModifierType.HIDDEN,
      matchType: 'all',
      conditions: []
    };
    handleFieldUpdate({
      modifiers: [...(selectedField.modifiers || []), newModifier]
    });
  };

  const removeModifier = (modId: string) => {
      if (!selectedField) return;
    handleFieldUpdate({
      modifiers: (selectedField.modifiers || []).filter(m => m.id !== modId)
    });
  };

  const updateModifier = (modId: string, updates: Partial<Modifier>) => {
      if (!selectedField) return;
    handleFieldUpdate({
      modifiers: (selectedField.modifiers || []).map(m => m.id === modId ? { ...m, ...updates } : m)
    });
  };

  const addCondition = (modId: string) => {
    if (!selectedField) return;
    const modifier = selectedField.modifiers?.find(m => m.id === modId);
    if (!modifier) return;
    const availableSourceFields = state.form.fields.filter(f => f.id !== selectedField.id);
    const newCondition: Condition = {
      id: `cond_${Date.now()}`,
      sourceFieldId: availableSourceFields[0]?.id || '',
      operator: ConditionOperator.EQUALS,
      value: ''
    };
    updateModifier(modId, {
      conditions: [...modifier.conditions, newCondition]
    });
  };

  const removeCondition = (modId: string, condId: string) => {
      if (!selectedField) return;
    const modifier = selectedField.modifiers?.find(m => m.id === modId);
    if (!modifier) return;
    updateModifier(modId, {
      conditions: modifier.conditions.filter(c => c.id !== condId)
    });
  };

  const updateCondition = (modId: string, condId: string, updates: Partial<Condition>) => {
      if (!selectedField) return;
    const modifier = selectedField.modifiers?.find(m => m.id === modId);
    if (!modifier) return;
    updateModifier(modId, {
      conditions: modifier.conditions.map(c => c.id === condId ? { ...c, ...updates } : c)
    });
  };

  const commonInputClass = "w-full p-2 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 placeholder-slate-400 transition-all text-slate-700";

  // RENDER GROUP PROPERTIES
  if (selectedGroup) {
      return (
        <aside className="w-80 bg-white border-l border-slate-200 h-[calc(100vh-64px)] overflow-y-auto">
            <div className="p-4 border-b border-slate-200 bg-slate-50/50">
                <h3 className="font-bold text-slate-800 flex items-center gap-2">
                    <Folder size={18} className="text-teal-600" />
                    Group Properties
                </h3>
            </div>
            <div className="p-4 space-y-6">
                <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Group ID</label>
                    <input 
                        type="text" 
                        value={localId}
                        onChange={(e) => handleIdChange(e.target.value)}
                        className={`${commonInputClass} font-mono ${idError ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''}`}
                    />
                    {idError && <p className="text-xs text-red-500 mt-1">{idError}</p>}
                </div>
                <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Label</label>
                    <input 
                        type="text" 
                        value={selectedGroup.label}
                        onChange={(e) => handleGroupUpdate({ label: e.target.value })}
                        className={commonInputClass}
                    />
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <span className="text-sm font-medium text-slate-700">Collapsible</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                            type="checkbox" 
                            checked={selectedGroup.collapsible || false} 
                            onChange={(e) => handleGroupUpdate({ collapsible: e.target.checked })}
                            className="sr-only peer" 
                        />
                         <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-teal-600"></div>
                    </label>
                </div>
                {selectedGroup.collapsible && (
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                        <span className="text-sm font-medium text-slate-700">Collapsed Default</span>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input 
                                type="checkbox" 
                                checked={selectedGroup.defaultCollapsed || false} 
                                onChange={(e) => handleGroupUpdate({ defaultCollapsed: e.target.checked })}
                                className="sr-only peer" 
                            />
                            <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-teal-600"></div>
                        </label>
                    </div>
                )}
            </div>
        </aside>
      );
  }

  // RENDER FORM PROPERTIES (Fallback)
  if (!selectedField) {
    return (
      <aside className="w-80 bg-white border-l border-slate-200 h-[calc(100vh-64px)] overflow-y-auto">
        <div className="p-4 border-b border-slate-200 bg-slate-50/50">
            <h3 className="font-bold text-slate-800 flex items-center gap-2">
                <FileText size={18} className="text-teal-600" />
                Form Properties
            </h3>
        </div>
        <div className="p-4 space-y-6">
             <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Form Name</label>
                <input 
                    type="text" 
                    value={state.form.name}
                    onChange={(e) => dispatch({ type: 'SET_FORM_NAME', payload: e.target.value })}
                    className={commonInputClass}
                />
            </div>

            <div>
                 <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Modifiers Script</label>
                 <textarea
                    value={state.form.modifiersScript || ''}
                    onChange={(e) => dispatch({ type: 'SET_FORM_MODIFIERS_SCRIPT', payload: e.target.value })}
                    className={`${commonInputClass} h-40 font-mono text-xs`}
                    placeholder="Late-Bound Eval Script..."
                 />
                 <p className="text-[10px] text-slate-400 mt-1">Global script modifiers logic (Optional)</p>
            </div>
            
            <div className="text-center pt-10 text-slate-400">
                <Settings size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">Select a field or group to configure details.</p>
            </div>
        </div>
      </aside>
    );
  }

  const availableSourceFields = state.form.fields.filter(f => f.id !== selectedField.id);

  return (
    <aside className="w-80 bg-white border-l border-slate-200 h-[calc(100vh-64px)] overflow-y-auto">
      <div className="p-4 border-b border-slate-200 bg-slate-50/50">
        <h3 className="font-bold text-slate-800 flex items-center gap-2">
            <Sliders size={18} className="text-teal-600" />
            Field Properties
        </h3>
      </div>

      <div className="p-4 space-y-6">
        {/* Basic Info */}
        <section className="space-y-4">
            <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Field ID</label>
                <input 
                    type="text" 
                    value={localId}
                    onChange={(e) => handleIdChange(e.target.value)}
                    className={`${commonInputClass} font-mono ${idError ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''}`}
                />
                {idError && <p className="text-xs text-red-500 mt-1">{idError}</p>}
            </div>
            
            <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Label</label>
                <input 
                    type="text" 
                    value={selectedField.label}
                    onChange={(e) => handleFieldUpdate({ label: e.target.value })}
                    className={commonInputClass}
                />
            </div>

            <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Input Type</label>
                <select 
                    value={selectedField.inputControl}
                    onChange={(e) => handleFieldUpdate({ inputControl: e.target.value as InputControl })}
                    className={commonInputClass}
                >
                    {Object.values(InputControl).map(c => (
                        <option key={c} value={c}>{c}</option>
                    ))}
                </select>
            </div>
            
            <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Default Value</label>
                <input 
                    type="text" 
                    value={selectedField.defaultValue || ''}
                    onChange={(e) => handleFieldUpdate({ defaultValue: e.target.value })}
                    className={commonInputClass}
                    placeholder="Default Value..."
                />
            </div>
        </section>

        <hr className="border-slate-100" />

        {/* Validation & States */}
        <section className="space-y-4">
            <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                <AlertCircle size={14} className="text-teal-500" /> Flags
            </h4>
            
            <div className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg border border-slate-200">
                <span className="text-sm font-medium text-slate-700">Required</span>
                <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                        type="checkbox" 
                        checked={selectedField.validation?.required || false} 
                        onChange={(e) => handleFieldUpdate({ validation: { ...selectedField.validation, required: e.target.checked } })}
                        className="sr-only peer" 
                    />
                    <div className="w-8 h-4 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-teal-600"></div>
                </label>
            </div>

            <div className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg border border-slate-200">
                <span className="text-sm font-medium text-slate-700">Disabled</span>
                <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                        type="checkbox" 
                        checked={selectedField.disabled || false} 
                        onChange={(e) => handleFieldUpdate({ disabled: e.target.checked })}
                        className="sr-only peer" 
                    />
                     <div className="w-8 h-4 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-teal-600"></div>
                </label>
            </div>

            <div className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg border border-slate-200">
                <span className="text-sm font-medium text-slate-700">Hidden</span>
                <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                        type="checkbox" 
                        checked={selectedField.hidden || false} 
                        onChange={(e) => handleFieldUpdate({ hidden: e.target.checked })}
                        className="sr-only peer" 
                    />
                     <div className="w-8 h-4 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-teal-600"></div>
                </label>
            </div>

            {selectedField.type === FieldType.NUMBER && (
                 <div className="grid grid-cols-2 gap-2 pt-2">
                    <div>
                        <label className="text-xs font-bold text-slate-500 mb-1 block uppercase">Min</label>
                        <input 
                            type="number"
                            value={selectedField.validation?.min || ''}
                            onChange={(e) => handleFieldUpdate({ validation: { ...selectedField.validation, min: parseInt(e.target.value) } })}
                            className={commonInputClass}
                        />
                    </div>
                    <div>
                        <label className="text-xs font-bold text-slate-500 mb-1 block uppercase">Max</label>
                        <input 
                             type="number"
                             value={selectedField.validation?.max || ''}
                             onChange={(e) => handleFieldUpdate({ validation: { ...selectedField.validation, max: parseInt(e.target.value) } })}
                             className={commonInputClass}
                        />
                    </div>
                 </div>
            )}
        </section>

        {/* Extensions */}
        <hr className="border-slate-100" />
        <section className="space-y-3">
             <div className="flex items-center justify-between">
                 <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2"><Blocks size={14} className="text-teal-500" /> Extensions</h4>
             </div>
             
             <div className="space-y-2">
                {Object.entries(selectedField.extensions || {}).map(([key, value]) => (
                    <div key={key} className="flex items-center gap-2 bg-slate-50 p-2 rounded border border-slate-200">
                         <div className="flex-1 min-w-0">
                             <div className="text-[10px] font-bold text-slate-500 uppercase truncate">{key}</div>
                             <div className="text-xs text-slate-800 truncate" title={value}>{value}</div>
                         </div>
                         <button onClick={() => removeExtension(key)} className="text-slate-400 hover:text-red-500">
                            <X size={14} />
                         </button>
                    </div>
                ))}
             </div>

             <div className="flex items-center gap-2">
                 <input 
                    placeholder="Key" 
                    className={`${commonInputClass} w-1/3 py-1 text-xs`}
                    value={newExtKey}
                    onChange={(e) => setNewExtKey(e.target.value)}
                 />
                 <input 
                    placeholder="Value" 
                    className={`${commonInputClass} flex-1 py-1 text-xs`}
                    value={newExtValue}
                    onChange={(e) => setNewExtValue(e.target.value)}
                 />
                 <button onClick={addExtension} disabled={!newExtKey} className="p-1.5 text-teal-600 bg-teal-50 hover:bg-teal-100 rounded disabled:opacity-50 transition-colors">
                    <Plus size={16} />
                 </button>
             </div>
        </section>

        {/* Parameter Configuration */}
        {selectedField.type === FieldType.PARAMETER && (
            <>
                <hr className="border-slate-100" />
                <section className="space-y-4">
                    <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                        <Database size={14} className="text-teal-500" /> Parameter Config
                    </h4>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Route Key</label>
                        <input
                            type="text"
                            value={selectedField.parameterConfig?.routeKey || ''}
                            onChange={(e) => updateParameterConfig('routeKey', e.target.value)}
                            className={commonInputClass}
                            placeholder="e.g. orderId"
                        />
                    </div>
                     <div>
                        <DataSourceBuilder 
                            value={selectedField.datasourceString || ''} 
                            onChange={(val) => handleFieldUpdate({ datasourceString: val })} 
                        />
                    </div>
                </section>
            </>
        )}

        {/* Dialog Field Configuration */}
        {selectedField.type === FieldType.DIALOG && (
            <>
                <hr className="border-slate-100" />
                <section className="space-y-4">
                    <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                        <Database size={14} className="text-teal-500" /> Dialog Data Source
                    </h4>
                    <DataSourceBuilder 
                        value={selectedField.datasourceString || ''} 
                        onChange={(val) => handleFieldUpdate({ datasourceString: val })} 
                    />
                </section>
            </>
        )}

        {/* Select/Radio Data Source Configuration */}
        {(selectedField.type === FieldType.ENUM || selectedField.inputControl === InputControl.SELECT || selectedField.inputControl === InputControl.RADIO) && (
            <>
                <hr className="border-slate-100" />
                <section className="space-y-3">
                    <div className="flex items-center justify-between">
                         <h4 className="text-sm font-bold text-slate-800">Options</h4>
                         <div className="flex bg-slate-100 p-0.5 rounded-lg border border-slate-200">
                             <button 
                                onClick={() => setDatasourceMode('static')}
                                className={`px-2 py-0.5 text-xs rounded-md transition-all font-medium ${datasourceMode === 'static' ? 'bg-white shadow-sm text-teal-600' : 'text-slate-500 hover:text-slate-700'}`}
                             >
                                 Static
                             </button>
                             <button 
                                onClick={() => setDatasourceMode('dynamic')}
                                className={`px-2 py-0.5 text-xs rounded-md transition-all font-medium ${datasourceMode === 'dynamic' ? 'bg-white shadow-sm text-teal-600' : 'text-slate-500 hover:text-slate-700'}`}
                             >
                                 Dynamic
                             </button>
                         </div>
                    </div>
                    
                    {datasourceMode === 'static' ? (
                        <div className="space-y-2">
                            <div className="flex justify-end mb-2">
                                <button onClick={addOption} className="text-xs flex items-center gap-1 text-teal-600 hover:text-teal-700 font-bold uppercase tracking-wider">
                                    <Plus size={12} /> Add Option
                                </button>
                            </div>
                            {(selectedField.dataSource || []).map((opt, idx) => (
                                <div key={idx} className="flex items-center gap-2">
                                    <input 
                                        className={`${commonInputClass} py-1 text-sm`}
                                        placeholder="Value"
                                        value={opt.value}
                                        onChange={(e) => updateOption(idx, 'value', e.target.value)}
                                    />
                                    <button onClick={() => removeOption(idx)} className="text-slate-400 hover:text-red-500">
                                        <X size={14} />
                                    </button>
                                </div>
                            ))}
                            {(!selectedField.dataSource || selectedField.dataSource.length === 0) && (
                                <div className="text-xs text-slate-400 italic text-center py-2 bg-slate-50 rounded border border-dashed border-slate-200">
                                    No options defined.
                                </div>
                            )}
                        </div>
                    ) : (
                        <DataSourceBuilder 
                            value={selectedField.datasourceString || ''} 
                            onChange={(val) => handleFieldUpdate({ datasourceString: val })} 
                        />
                    )}
                </section>
            </>
        )}

        <hr className="border-slate-100" />

        {/* Conditional Logic */}
        <section className="space-y-4">
             <div className="flex items-center justify-between">
                <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                    <GitBranch size={14} className="text-teal-500" /> Visual Logic
                </h4>
                <button onClick={addModifier} className="text-xs flex items-center gap-1 text-teal-600 hover:text-teal-700 font-bold uppercase tracking-wider">
                    <Plus size={12} /> Add Rule
                </button>
            </div>

            {selectedField.modifiers?.map((modifier) => (
                <div key={modifier.id} className="bg-slate-50 rounded-lg border border-slate-200 p-3 space-y-3">
                    <div className="flex items-center justify-between">
                        <select
                            value={modifier.type}
                            onChange={(e) => updateModifier(modifier.id, { type: e.target.value as ModifierType })}
                            className="bg-white border border-slate-300 text-xs rounded px-2 py-1 font-medium text-slate-700 focus:outline-none focus:border-teal-500"
                        >
                            <option value={ModifierType.HIDDEN}>Hide this field</option>
                            <option value={ModifierType.REQUIRED}>Make required</option>
                            <option value={ModifierType.DISABLED}>Disable this field</option>
                        </select>
                        <button onClick={() => removeModifier(modifier.id)} className="text-slate-400 hover:text-red-500">
                            <Trash2 size={14} />
                        </button>
                    </div>

                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">WHEN</div>

                    <div className="space-y-2">
                        {modifier.conditions.map((cond) => (
                            <div key={cond.id} className="bg-white p-2 rounded border border-slate-200 space-y-2">
                                <div className="flex justify-between items-start">
                                    <select 
                                        value={cond.sourceFieldId}
                                        onChange={(e) => updateCondition(modifier.id, cond.id, { sourceFieldId: e.target.value })}
                                        className="w-full text-xs border-slate-300 border rounded py-1 px-1 mb-1 focus:border-teal-500 focus:outline-none text-slate-700"
                                    >
                                        <option value="" disabled>Select Field</option>
                                        {availableSourceFields.map(f => (
                                            <option key={f.id} value={f.id}>{f.label || f.id}</option>
                                        ))}
                                    </select>
                                    <button onClick={() => removeCondition(modifier.id, cond.id)} className="ml-1 text-slate-300 hover:text-red-500">
                                        <X size={12} />
                                    </button>
                                </div>
                                <div className="flex gap-1">
                                    <select
                                        value={cond.operator}
                                        onChange={(e) => updateCondition(modifier.id, cond.id, { operator: e.target.value as ConditionOperator })}
                                        className="w-1/2 text-xs border-slate-300 border rounded py-1 px-1 focus:border-teal-500 focus:outline-none text-slate-700"
                                    >
                                        {Object.values(ConditionOperator).map(op => (
                                            <option key={op} value={op}>{op.replace(/_/g, ' ')}</option>
                                        ))}
                                    </select>
                                    {!['is_true', 'is_false', 'is_empty', 'is_not_empty'].includes(cond.operator) && (
                                        <input
                                            type="text"
                                            value={cond.value}
                                            onChange={(e) => updateCondition(modifier.id, cond.id, { value: e.target.value })}
                                            placeholder="Value"
                                            className="w-1/2 text-xs border-slate-300 border rounded py-1 px-1 focus:border-teal-500 focus:outline-none text-slate-700"
                                        />
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                     <button onClick={() => addCondition(modifier.id)} className="w-full py-1 text-xs text-slate-500 border border-dashed border-slate-300 rounded hover:bg-white hover:text-teal-600 hover:border-teal-300 transition-colors">
                        + Add Condition
                    </button>
                </div>
            ))}
            
            {(!selectedField.modifiers || selectedField.modifiers.length === 0) && (
                <div className="text-xs text-slate-400 italic text-center py-2">
                    No visual rules defined.
                </div>
            )}
        </section>
      </div>
    </aside>
  );
};